/**
 * CourseyVoyage English string table
 */
import type { Strings } from './types';

export const en: Strings = {
    'app.loading': 'Loading...',
    'app.or': 'or',
    'app.appName': 'CourseyVoyage',
    'app.backPressToExit': 'Press back again to exit',

    'nav.dashboard': 'Dashboard',
    'nav.schedule': 'Schedule',
    'nav.courses': 'Course Mgmt',
    'nav.skills': 'Learning',
    'nav.finance': 'Finance',
    'nav.settings': 'Settings',

    'login.welcome': 'Welcome back',
    'login.loggingIn': 'Logging in...',
    'login.guest': 'Guest login',
    'login.guestConfirmTitle': 'Guest Login Confirmation',
    'login.guestConfirmImportant': 'Important:',
    'login.guestConfirmNote': 'Guest mode data will be cleared when you sign out. Upgrade to a full account to keep your data.',
    'login.guestConfirmQuestion': 'Continue with guest login?',
    'login.cancel': 'Cancel',
    'login.confirmLogin': 'Confirm',
    'login.readyToStart': 'Ready to start learning?',
    'login.joinUs': 'Join us to help manage your children\'s courses and prepare for their future.',
    'login.googleLogin': 'Sign in with Google',
    'login.failed': 'Login failed',
    'login.webviewWarningTitle': 'You are using an in-app browser',
    'login.webviewWarningDesc': 'Google sign-in may not work here. Please open this site in your system browser (Chrome, Safari, etc.).',
    'login.webviewGoogleDisabled': 'Google sign-in is not available in the in-app browser. Please open this site in your system browser.',

    'bubble.welcome': 'Welcome to CourseyVoyage!',
    'bubble.rememberCourse': "Remember to complete today's lessons!",
    'bubble.wellDone': 'Well done! Keep it up!',
    'bubble.howIsLearning': 'How was your learning today?',
    'bubble.needHelp': 'Need help?',
    'bubble.doubleTapToSwitch': 'Double-tap to switch child',
    'bubble.parentTip1': 'Tap avatar for tips',
    'bubble.parentTip2': 'Manage children in Settings',
    'bubble.parentTip3': 'View overview on Dashboard',
    'bubble.parentTip4': 'Add children or courses in Settings',
    'bubble.parentTip5': 'Add courses in Course Management',

    'welcome.noChildMessage': "No child added yet. Please add a child to start managing courses.",

    'child.all': 'All',
    'child.yearsOld': ' yrs',
    'child.edit': 'Edit',
    'child.delete': 'Delete',
    'child.addChild': '+ Add child',

    'settings.title': 'Settings',
    'settings.childManagement': 'Children',
    'settings.childManagementDesc': 'Tap avatar or name to switch the child you are viewing',
    'settings.notifications': 'Notifications',
    'settings.notifLessonLow': 'Lessons expiring soon',
    'settings.notifLessonLowDesc': 'Notify when remaining lessons are below threshold',
    'settings.notifPaymentDue': 'Payment due reminder',
    'settings.notifPaymentDueDesc': 'Notify 7 days and 3 days before due',
    'settings.notifDailyReminder': "Today's courses reminder",
    'settings.notifDailyReminderDesc': 'Remind 1 hour before classes each day',
    'settings.thresholdTitle': 'Lesson alert threshold',
    'settings.thresholdDesc': 'Show alert when remaining lessons are below this value',
    'settings.thresholdDefault': 'Default threshold',
    'settings.thresholdUnit': 'lessons',
    'settings.expiryAlertDays': 'Alert days before expiry',
    'settings.expiryAlertDaysUnit': 'days',
    'settings.account': 'Account',
    'settings.logout': 'Log out',
    'settings.logoutDesc': 'Sign out of your account and return to the login page',
    'settings.upgradeTitle': 'Upgrade to Full Account',
    'settings.upgradeDesc': 'Upgrade to keep your data backed up and synced across devices. All your data will be preserved.',
    'settings.upgradeWithGoogle': 'Upgrade with Google',
    'settings.upgrading': 'Upgrading...',
    'settings.upgradeSuccess': 'Upgrade successful! You can now sign in with your Google account.',
    'settings.anonymousLogoutTitle': 'Sign Out Confirmation',
    'settings.anonymousLogoutImportant': 'Important:',
    'settings.anonymousLogoutWarning': 'You are using guest mode. All data will be deleted when you sign out.',
    'settings.anonymousLogoutConfirm': 'Are you sure you want to sign out?',
    'settings.confirmLogout': 'Confirm sign out',
    'settings.aboutTitle': 'About CourseyVoyage',
    'settings.aboutVersion': 'Version {version}',
    'settings.aboutDesc': "Helps parents manage their children's extracurricular courses, lesson counts, and payment status.",
    'settings.language': 'Language',
    'settings.languageDesc': 'Choose interface language',
    'settings.exportTitle': 'Data export',
    'settings.exportDesc': 'Export all course data within the selected date range as an Excel file.',
    'settings.exportDateRange': 'Date range',
    'settings.exportButton': 'Export',
    'settings.exporting': 'Exporting...',
    'settings.exportSuccess': 'Export complete',
    'settings.exportEmpty': 'No course data in this range',
    'settings.exportError': 'Export failed. Please try again later',
    'export.reportTitle': 'Course Data Export',
    'export.dateRangeLabel': 'Date range',
    'export.generated': 'Generated',
    'export.noData': 'No course data in this range.',
    'export.date': 'Date',
    'export.amount': 'Amount',
    'export.note': 'Note',
    'export.recordType': 'Type',
    'export.sheetCourses': 'Courses',
    'export.sheetPayments': 'Payments',
    'export.sheetRecords': 'Records',
    'export.colChild': 'Child',
    'export.colCourseName': 'Course name',
    'export.colInstitution': 'Institution',
    'export.colTeacher': 'Teacher',
    'export.colStartDate': 'Start date',
    'export.colEndDate': 'End date',
    'export.colTotalLessons': 'Total lessons',
    'export.colUsedLessons': 'Used lessons',
    'export.colStatus': 'Status',
    'export.colCourseFee': 'Course fee',
    'export.sectionPayments': 'Payments in range',
    'export.sectionRecords': 'Records in range',
    'export.colRemainingLessons': 'Remaining',
    'export.colTotalPaid': 'Total paid',
    'export.colTotalRefunded': 'Refunded',
    'export.colNetPaid': 'Net paid',
    'export.colRemainingPayment': 'Remaining to pay',
    'export.paymentStatusPaid': 'Paid',
    'export.paymentStatusUnpaid': 'Unpaid',
    'export.colEffectiveEndDate': 'Effective end',
    'export.sectionAllPayments': 'Payment records',
    'export.sectionAllRefunds': 'Refund records',
    'export.scheduleWeekPrefix': 'Week',
    'export.recordCompleted': 'Completed',
    'export.recordLeave': 'Leave',
    'export.recordCancelled': 'Cancelled',
    'export.recordMakeup': 'Makeup',
    'export.colSchedule': 'Schedule',
    'export.colMonth': 'Month',

    'modal.addChild': 'Add child',
    'modal.editChild': 'Edit child',
    'modal.deleteChild': 'Delete child',
    'modal.childName': 'Name',
    'modal.childNamePlaceholder': "Enter child's name",
    'modal.gender': 'Gender',
    'modal.genderMale': 'Male',
    'modal.genderFemale': 'Female',
    'modal.birthdate': 'Birthdate',
    'modal.avatar': 'Avatar',
    'modal.cancel': 'Cancel',
    'modal.skip': 'Later',
    'modal.save': 'Save',
    'modal.saving': 'Saving...',
    'modal.add': 'Add',
    'modal.delete': 'Delete',
    'modal.deleting': 'Deleting...',
    'modal.confirmDeleteChild': 'Delete "{name}"? This cannot be undone.',
    'modal.confirmDeleteChildNote': 'This cannot be undone',
    'modal.duplicateChildName': 'A child with this name already exists',
    'modal.addFailed': 'Add failed. Please try again.',
    'modal.updateFailed': 'Update failed. Please try again.',
    'modal.deleteFailed': 'Delete failed. Please try again.',

    'dashboard.title': 'Dashboard',
    'dashboard.activeCourses': 'Active courses',
    'dashboard.todayCourses': "Today's courses",
    'dashboard.pendingPayments': 'Unpaid',
    'dashboard.lessonLowAlert': 'Low lesson count',
    'dashboard.pendingPaymentsAlert': '{count} unpaid',
    'dashboard.checkFinance': 'Check in Finance',
    'dashboard.noCoursesToday': 'No courses today. Rest well!',
    'dashboard.courseList': 'Course list',
    'dashboard.checkIn': 'Check in',
    'dashboard.checkInSuccess': '✅ Check-in recorded!',
    'dashboard.remaining': 'Remaining',
    'dashboard.remainingToPay': 'Remaining to pay',
    'dashboard.lessons': 'lessons',
    'dashboard.payButton': 'Pay',

    'courses.title': 'Courses',
    'courses.filterAll': 'All',
    'courses.filterActive': 'Active',
    'courses.filterLow': 'Expiring soon',
    'courses.filterExpired': 'Unpaid',
    'courses.filterPaid': 'Paid',
    'courses.filterDone': 'Completed',
    'courses.filterPaused': 'Paused',
    'courses.sortBy': 'Sort by',
    'courses.sortByCreatedAt': 'Created',
    'courses.sortByStartDate': 'Start date',
    'courses.sortByEndDate': 'End date',
    'courses.sortByName': 'Course name',
    'courses.sortByTotalLessons': 'Total lessons',
    'courses.sortByRemainingLessons': 'Remaining lessons',
    'courses.sortByFee': 'Fee',
    'courses.sortOrderAsc': 'Ascending (click to switch to descending)',
    'courses.sortOrderDesc': 'Descending (click to switch to ascending)',
    'courses.loading': 'Loading...',
    'courses.empty': 'No matching courses',
    'courses.emptyHint': 'Tap + to add a course',
    'courses.pageInfo': 'Page {current} / {total}',
    'courses.addCourse': 'Add course',
    'courses.addChildFirst': 'Please add a child first',
    'courses.addFailed': 'Add failed. Please try again.',
    'courses.dateRange': '{start} ～ {end}',
    'courses.dateRangeStartOnly': '{start} ～',
    'courses.totalPaid': 'Total Paid',
    'courses.totalRefunded': 'Total Refunded',
    'courses.netPaid': 'Net Paid',
    'courses.pleaseFillCourseName': 'Please enter course name',
    'courses.pleaseFillStartDate': 'Please select start date',
    'courses.courseFeeInvalid': 'Course fee cannot be negative',
    'courses.pleaseFillCourseFee': 'Please enter course fee',
    'courses.courseName': 'Course name *',
    'courses.courseNamePlaceholder': 'e.g. English conversation',
    'courses.courseType': 'Course type',
    'courses.institution': 'Institution',
    'courses.institutionPlaceholder': 'e.g. ABC School',
    'courses.teacher': 'Teacher',
    'courses.teacherPlaceholder': 'e.g. Mr. Wang',
    'courses.optionalSuffix': ' (optional)',
    'courses.weekdays': 'Weekdays',
    'courses.startTime': 'Start time',
    'courses.endTime': 'End time',
    'courses.location': 'Location',
    'courses.locationPlaceholder': 'e.g. 123 Example Ave (or room name)',
    'courses.startDate': 'Start date',
    'courses.endDate': 'End date',
    'courses.byLessons': 'By total lessons',
    'courses.byEndDate': 'By end date',
    'courses.courseFee': 'Course fee',
    'courses.courseFeePlaceholder': 'e.g. 9000 (NT$)',
    'courses.paymentMode': 'Payment mode',
    'courses.paymentFull': 'Full payment',
    'courses.paymentMonthly': 'Monthly payment',
    'courses.paymentPerClass': 'Per class',
    'courses.monthlyFee': 'Monthly amount',
    'courses.perClassFee': 'Amount per class',
    'courses.perClassFeePlaceholder': 'e.g. 500',
    'courses.monthlyFeePlaceholder': 'e.g. 3000',
    'courses.dueDayOfMonth': 'Due day of month',
    'courses.dueByDay': 'Due by day {day}',
    'courses.dueDayOfMonthPlaceholder': 'e.g. 5 (by 5th)',
    'courses.reminderDaysBefore': 'Reminder days before due',
    'courses.reminderDaysBeforePlaceholder': 'e.g. 3',
    'courses.pleaseFillDueDayOfMonth': 'Please enter due day of month',
    'courses.pleaseFillReminderDaysBefore': 'Please enter reminder days before due date',
    'courses.totalLessons': 'Total lessons',
    'courses.pleaseFillTotalLessons': 'Please enter total lessons',
    'courses.pleaseFillEndDate': 'Please select end date',
    'courses.pleaseSelectWeekdays': 'Please select weekdays first when using end date',
    'courses.selectWeekday': 'Select weekday',
    'courses.addSlot': 'Add slot',
    'courses.scheduleOverlap': 'Time slots on the same day cannot overlap',
    'courses.noLessonsInRange': 'No lessons found in the date range for selected weekdays',
    'courses.totalLessonsLessThanUsed': 'Computed total lessons ({computed}) cannot be less than lessons already used ({used}). Please adjust dates or weekdays.',
    'courses.endTimeMustAfterStart': 'End time cannot be earlier than start time',
    'courses.alertThreshold': 'Alert when remaining below',
    'courses.courseColor': 'Color',
    'courses.status': 'Status',
    'courses.editCourse': 'Edit course',
    'courses.confirmDeleteCourse': 'Delete "{name}"? The entire course (including records and payment history) will be permanently removed. This cannot be undone.',
    'courses.updateFailed': 'Update failed. Please try again.',
    'courses.deleteFailed': 'Delete failed. Please try again.',
    'courses.pauseCourse': 'Pause course',
    'courses.resumeCourse': 'Resume course',
    'courses.copyCourse': 'Copy',
    'courses.leaveDeductsLesson': 'Deduct lesson when absent (leave)',

    'courseType.語言': 'Language',
    'courseType.音樂': 'Music',
    'courseType.體育': 'Sports',
    'courseType.學科': 'Academic',
    'courseType.才藝': 'Performing Arts',
    'courseType.美術': 'Fine Arts',
    'courseType.其他': 'Other',

    'status.active': 'Active',
    'status.low': 'Expiring soon',
    'status.expired': 'Unpaid',
    'status.paymentPaid': 'Paid',
    'status.paused': 'Paused',
    'status.done': 'Done',

    'detail.back': 'Back',
    'detail.edit': 'Edit',
    'detail.delete': 'Delete',
    'detail.remainingLessons': 'Remaining',
    'detail.usedLessons': 'Used',
    'detail.totalLessons': 'Total',
    'detail.usedPercent': '{pct}% used',
    'detail.completed': '✅ Completed',
    'detail.leave': '🤒 Leave',
    'detail.makeup': '🔁 Makeup',
    'detail.cancelled': '🎒 Absence',
    'detail.courseInfo': '📋 Course info',
    'detail.institution': 'Institution',
    'detail.teacher': 'Teacher',
    'detail.weekdays': 'Weekdays',
    'detail.timeRange': 'Time',
    'detail.startDate': 'Start date',
    'detail.endDate': 'End date',
    'detail.location': 'Location',
    'detail.leaveDeductsLesson': 'Leave deducts lesson',
    'detail.leaveDeductsLessonYes': 'Yes',
    'detail.leaveDeductsLessonNo': 'No',
    'detail.courseFee': 'Course fee',
    'detail.contact': 'Contact',
    'detail.belongsTo': 'Child',
    'detail.records': '📅 Records',
    'detail.noRecords': 'No records yet',
    'detail.unrecordedLessons': 'Unrecorded',
    'detail.noUnrecordedLessons': 'No unrecorded lessons',
    'detail.futureLessons': 'Future lessons',
    'detail.noFutureLessons': 'No future lessons',
    'detail.editFutureLesson': 'Edit date/time',
    'detail.futureEditOriginal': 'Original slot',
    'detail.futureEditDate': 'Date',
    'detail.futureEditTime': 'Time',
    'detail.futureEditDateMustBeFuture': 'Please choose a date after today',
    'detail.futureEditDateExceedsCourseEnd': 'Date cannot be later than the course end date ({end})',
    'detail.futureEditTargetHasRecord': 'There is already a record for this date/time. Please choose another slot.',
    'detail.confirmEditFutureLesson': 'Reschedule {fromDate} {fromStart}-{fromEnd} to {toDate} {toStart}-{toEnd}?',
    'detail.futureEditFailed': 'Failed to update future lesson',
    'detail.prevPage': 'Prev',
    'detail.nextPage': 'Next',
    'detail.paymentRecords': '💰 Payments',
    'detail.noPaymentRecords': 'No payment records',
    'detail.addPayment': '+ Add payment',
    'detail.loading': 'Loading...',
    'detail.notFound': 'Course not found',
    'detail.backToCourses': 'Back to courses',
    'detail.recordFailed': 'Record failed. Please try again.',
    'detail.makeupDate': 'Makeup date',
    'detail.makeupTime': 'Time',
    'detail.makeupNote': 'Note (optional)',
    'detail.addMakeup': 'Add makeup',
    'detail.makeupExceedsTotal': 'This course is session-based. No remaining sessions; cannot add makeup.',
    'detail.makeupDateNotFuture': 'Makeup date cannot be in the future. Please select today or an earlier date.',
    'detail.addInsertedLesson': '➕ Add lesson',
    'detail.insertedLessonDate': 'Lesson date',
    'detail.insertedLessonTag': 'Inserted makeup',
    'detail.editInsertedLesson': 'Edit inserted lesson note',
    'detail.removeInsertedLesson': 'Remove inserted lesson',
    'detail.confirmRemoveInserted': 'Remove this inserted lesson?',
    'detail.insertedExceedsTotal': 'Not enough remaining sessions to add a lesson.',
    'detail.insertedDateMustBeFuture': 'Please select tomorrow or a later date.',
    'detail.insertedDateBeforeStart': 'Date cannot be before the course start date.',
    'detail.insertedDateAfterEnd': 'Date cannot be after the course end date.',
    'detail.insertedPausedNotAllowed': 'Course is paused. Cannot insert a lesson on or after the pause date.',
    'detail.insertedSlotConflict': 'This date and time slot is already taken.',
    'detail.insertedTimeOverlap': 'This time overlaps with another lesson on the same day.',
    'detail.insertedEndTimeBeforeStart': 'End time must be after start time.',
    'detail.editRecord': 'Edit',
    'detail.deleteRecord': 'Delete',
    'detail.confirmDeleteRecord': 'Delete this record?',
    'detail.note': 'Note',
    'detail.refundRecords': 'Refunds',
    'detail.refundDate': 'Refund date',
    'detail.addRefund': '+ Add refund',
    'detail.editPayment': 'Edit payment',
    'detail.confirmDeletePayment': 'Delete this payment record?',
    'detail.noRefundRecords': 'No refund records yet',
    'detail.noRefundableAmount': 'No refundable amount',
    'detail.refundExceedsPayments': 'Refund cannot exceed total paid. Max: NT$ {max}',
    'detail.refundMaxHint': 'Max refundable: NT$ {max}',

    'payment.add': 'Add payment',
    'payment.date': 'Payment date',
    'payment.amount': 'Amount (NT$) *',
    'payment.month': 'Payment month',
    'payment.monthPlaceholder': 'e.g. 2025-03',
    'payment.amountPlaceholder': 'e.g. 9000',
    'payment.lessons': 'Lessons purchased',
    'payment.lessonsPlaceholder': 'e.g. 30',
    'payment.method': 'Payment method',
    'payment.status': 'Payment status',
    'payment.dueDate': 'Due date',
    'payment.note': 'Note (optional)',
    'payment.notePlaceholder': 'e.g. First term',
    'payment.perClassAmountHint': 'Per-class courses auto-calculate amount by estimated lessons; please adjust payment to actual situation.',
    'payment.receipt': 'Payment receipt',
    'payment.receiptSelected': 'Selected: {name}',
    'payment.receiptRemove': 'Remove',
    'payment.receiptRemoveExisting': 'Remove receipt',
    'payment.receiptView': 'View receipt',
    'payment.receiptNone': 'No receipt uploaded',
    'payment.receiptUploadFailed': 'Receipt upload failed. Please try again.',
    'payment.receiptAlt': 'Payment receipt',
    'payment.receiptAltCurrent': 'Current receipt',

    'paymentMethod.現金': 'Cash',
    'paymentMethod.匯款': 'Transfer',
    'paymentMethod.信用卡': 'Credit card',
    'paymentMethod.LINE Pay': 'LINE Pay',

    'paymentStatus.paid': 'Paid',
    'paymentStatus.pending': 'Pending',
    'paymentStatus.overdue': 'Overdue',

    'finance.title': 'Finance',
    'finance.paid': '✅ Paid',
    'finance.pending': '⏳ Pending',
    'finance.totalRemainingEstimate': 'Total remaining (est.)',
    'finance.overdue': '🔴 Overdue',
    'finance.categoryChart': 'Spending by category',
    'finance.monthlyChart': 'Last 6 months',
    'finance.noItems': 'No {status} items',
    'finance.markPaid': 'Mark paid',
    'finance.markPaidSuccess': '✅ Marked as paid!',
    'finance.dueDate': 'Due: ',
    'finance.paymentRefundRecords': 'Payments & refunds',

    'schedule.title': 'Schedule',
    'schedule.dayView': 'Day',
    'schedule.weekView': 'Week',
    'schedule.monthView': 'Month',
    'schedule.week': 'Week',
    'schedule.monthYear': '{month}/{year}',
    'schedule.monthPart': '{month}',
    'schedule.prevDay': 'Prev day',
    'schedule.nextDay': 'Next day',
    'schedule.prevWeek': 'Prev week',
    'schedule.nextWeek': 'Next week',
    'schedule.goToToday': 'Today',
    'schedule.prevMonth': 'Prev month',
    'schedule.nextMonth': 'Next month',
    'schedule.actionCheckIn': 'Check in',
    'schedule.actionLeave': 'Leave',
    'schedule.actionAbsence': 'Absence',
    'schedule.confirmRecord': 'Mark as "{action}"?',

    'weekday.sun': 'Sun',
    'weekday.mon': 'Mon',
    'weekday.tue': 'Tue',
    'weekday.wed': 'Wed',
    'weekday.thu': 'Thu',
    'weekday.fri': 'Fri',
    'weekday.sat': 'Sat',

    'skills.title': 'Learning History',
    'skills.intro': 'Track your child\'s courses and skills. More detailed analytics coming soon.',
    'skills.activeByType': 'Active courses',
    'skills.overview': 'Skills overview',
    'skills.timelineToday': "Today's Schedule",
    'skills.timelineByDate': 'Course Date Timeline',
    'skills.timelineEmpty': 'No course dates',
    'skills.preview': 'Preview',
    'skills.lessonsAttended': '~{hours} lessons attended',
    'skills.comingSoon': 'Full skills analysis is under development. Stay tuned!',
    'skills.empty': 'No course records yet',
    'skills.emptyHint': 'Learning history will accumulate after you add courses',
    'skills.goToAddCourse': 'Add courses',
    'skills.timelineRangeLabel': 'Visible range',
    'skills.timelineFrom': 'From',
    'skills.timelineTo': 'To',
    'skills.timelineReset': 'All',
    'skills.typeLang': 'Language',
    'skills.typeMusic': 'Music',
    'skills.typeSports': 'Sports',
    'skills.typeAcademic': 'Academic',
    'skills.typeTalent': 'Performing Arts',
    'skills.typeFineArts': 'Fine Arts',
    'skills.typeOther': 'Other',

    'offline.modal.title': 'Offline',
    'offline.modal.description': 'You are viewing cached data. You can continue using the app. Changes will sync when connection is restored.',
    'offline.modal.confirm': 'OK',
    'offline.recovered.title': 'Connection restored',
    'offline.recovered.description': 'Network is back. Reload to get the latest data.',
    'offline.recovered.reload': 'Reload now',
    'offline.recovered.later': 'Later',
    'offline.noData.title': 'Cannot load',
    'offline.noData.description': 'Offline with no cached data. Please connect to the internet and refresh.',
    'offline.noData.reload': 'Refresh',

    'install.title': 'Install {name} on your device',
    'install.titleInstalled': '{name} installed',
    'install.howToInstall': 'How to install {name}',
    'install.benefits': 'Benefits after installing:',
    'install.benefit1': 'Faster loading',
    'install.benefit2': 'Native app experience, full-screen display',
    'install.benefit3': 'Instant notifications',
    'install.benefit4': 'Offline use',
    'install.installing': 'Installing...',
    'install.installNow': 'Install now',
    'install.viewInstructions': 'View instructions',
    'install.oneClickNote': 'Tap the button above to install quickly',
    'install.manualNote': 'We will guide you through the installation',
    'install.dontShowAgain': 'Don\'t show again',
    'install.later': 'Later',
    'install.useDesktopApp': 'Use desktop app',
    'install.useDesktopNote': 'CourseyVoyage is installed!',
    'install.continueBrowser': 'Continue in browser',
    'install.desktopHint': 'For the best experience, use the installed desktop app:',
    'install.back': 'Back',
    'install.youAreUsing': 'You are using:',

    'auth.notLoggedIn': 'User is not signed in',
};
